<template>
  <div class="kitchen-display">
    <v-container fluid class="pa-4">
      <!-- Header -->
      <div class="d-flex align-center justify-space-between mb-6">
        <h1 class="text-h4 font-weight-bold">Kitchen Display</h1>
        <div class="d-flex align-center">
          <!-- Switch para Auto-refresh -->
          <div class="d-flex align-center mr-4">
            <v-switch
              v-model="autoRefresh"
              label="Auto-refresh"
              density="compact"
              color="primary"
              hide-details
              class="mt-0 pt-0"
            />
          </div>
          <!-- Chip para mostrar el número de órdenes activas -->
          <v-chip
            :color="kitchenOrders.length > 0 ? 'warning' : 'success'"
            size="large"
            class="orders-count"
          >
            {{ kitchenOrders.length }} Active Orders
          </v-chip>

          <!-- Botón de refresco con tooltip -->
          <v-tooltip text="Refresh orders" location="top">
            <template v-slot:activator="{ props }">
              <v-btn
                v-bind="props"
                @click="fetchOrders"
                color="info"
                size="small"
                class="mx-2"
                icon
              >
                <v-icon>mdi-refresh</v-icon>
              </v-btn>
            </template>
          </v-tooltip>
        </div>
      </div>

      <!-- Tabs -->
      <v-tabs
        v-model="activeTab"
        color="primary"
        class="mb-4 kitchen-tabs"
        fixed-tabs
        show-arrows
        height="56"
      >
        <v-tab
          value="active"
          class="kitchen-tab text-subtitle-1 font-weight-medium"
          :default="true"
        >
          <v-icon icon="mdi-clock-outline" class="mr-2" />
          Active Orders
          <v-chip
            size="x-small"
            color="warning"
            class="ml-2"
            v-if="kitchenOrders.length"
          >
            {{ kitchenOrders.length }}
          </v-chip>
        </v-tab>
        <v-tab value="history" class="kitchen-tab text-subtitle-1">
          <v-icon icon="mdi-history" class="mr-2" />
          Order History
          <v-chip
            size="x-small"
            color="success"
            class="ml-2"
            v-if="completedOrders.length"
          >
            {{ completedOrders.length }}
          </v-chip>
        </v-tab>
      </v-tabs>

      <!-- Tab Content -->
      <v-window v-model="activeTab" class="flex-grow-1 overflow-y-auto">
        <!-- Loading State -->
        <div v-if="loading" class="d-flex justify-center py-8">
          <v-progress-circular indeterminate color="primary" size="64" />
        </div>

        <template v-else>
          <!-- Active Orders Tab -->
          <v-window-item value="active">
            <div v-if="kitchenOrders.length === 0" class="text-center py-8">
              <v-icon
                icon="mdi-coffee-outline"
                size="64"
                color="grey-lighten-1"
                class="mb-4"
              />
              <h3 class="text-h6 text-grey-darken-1">No Active Orders</h3>
              <p class="text-body-1 text-medium-emphasis">
                All orders have been completed
              </p>
            </div>
            <div v-else>
              <!-- Mostrar las órdenes activas paginadas -->
              <v-row>
                <v-col
                  v-for="order in paginatedKitchenOrders"
                  :key="order.id"
                  cols="12"
                  sm="6"
                  lg="4"
                >
                  <kitchen-order-card
                    :order="order"
                    @updateOrder="order = $event"
                    @complete="handleOrderComplete($event)"
                  />
                </v-col>
              </v-row>

              <!-- Paginación -->
              <div class="text-center mt-6">
                <v-pagination
                  v-model="currentKitchenPage"
                  :length="totalKitchenPages"
                  :total-visible="7"
                  circle
                />
              </div>
            </div>
          </v-window-item>

          <!-- Order History Tab -->
          <v-window-item value="history">
            <div v-if="completedOrders.length === 0" class="text-center py-8">
              <v-icon
                icon="mdi-history"
                size="64"
                color="grey-lighten-1"
                class="mb-4"
              />
              <h3 class="text-h6 text-grey-darken-1">No Completed Orders</h3>
              <p class="text-body-1 text-medium-emphasis">
                Completed orders will appear here
              </p>
            </div>
            <div v-else>
              <!-- Mostrar las órdenes paginadas -->
              <v-row>
                <v-col
                  v-for="order in paginatedOrders"
                  :key="order.id"
                  cols="12"
                  sm="6"
                  lg="4"
                >
                  <kitchen-order-card :order="order" :completed="true" />
                </v-col>
              </v-row>

              <!-- Paginación -->
              <div class="text-center mt-6">
                <v-pagination
                  v-model="currentPage"
                  :length="totalPages"
                  :total-visible="7"
                  circle
                />
              </div>
            </div>
          </v-window-item>
        </template>
      </v-window>
    </v-container>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from "vue";
import { KitchenService } from "@/services/api/kitchen-service";
import KitchenOrderCard from "./components/KitchenOrderCard.vue";
import { useKitchenStore } from "@/stores/kitchen"; // <-- Add this

// Constants
const KITCHEN_SECTION_ID = 1;
const POLL_INTERVAL = 88000;
// Local state
const loading = ref(false);
const orders = ref([]);
const activeTab = ref("active"); // Default to 'active' tab
const autoRefresh = ref(true);
const refreshTimer = ref(null);
// Estado de la paginación
const currentPage = ref(1);
const itemsPerPage = ref(10);
const currentKitchenPage = ref(1);

// Computed properties
const kitchenOrders = computed(() => {
  return orders.value
    .filter((order) => {
      const hasKitchenItems = order.items?.some(
        (item) => item.pos_status === "P"
      );
      const isPending = order.pos_status === "P";
      const isValidType = ["INVOICE", "HOLD"].includes(
        order.type?.toUpperCase()
      );

      return hasKitchenItems && isPending && isValidType;
    })
    .sort((a, b) => {
      const dateA = new Date(a.invoice_date || a.created_at);
      const dateB = new Date(b.invoice_date || b.created_at);
      return dateB.getTime() - dateA.getTime();
    });
});

const completedOrders = computed(() => {
  return orders.value
    .filter((order) => {
      const hasKitchenItems = order.items?.some(
        (item) => item.pos_status === "C"
      );

      const isCompleted = order.pos_status === "C";
      const isValidType = ["INVOICE", "HOLD"].includes(
        order.type?.toUpperCase()
      );

      return hasKitchenItems && isCompleted && isValidType;
    })
    .sort((a, b) => {
      const dateA = new Date(a.completed_at || a.invoice_date || a.created_at);
      const dateB = new Date(b.completed_at || b.invoice_date || b.created_at);
      return dateB - dateA;
    });
});

async function fetchOrders() {
  loading.value = true;
  try {
    const fetchedOrders = await KitchenService.fetchOrders(KITCHEN_SECTION_ID);
    // Process and normalize orders
    orders.value = fetchedOrders.map((order) => {
      return {
        ...order,
        items: Array.isArray(order.items) ? order.items : [],
      };
    });
    console.log(orders.value[0])
  } catch (error) {
    console.error("❌ Error fetching orders:", error);
  } finally {
    loading.value = false;
  }
}

async function handleOrderComplete(orderId) {

  try {
    
    const order = orders.value.find((o) => o.id === orderId);

    if (!order) {
      console.error("❌ Orden no encontrada:", orderId);
      return;
    }

    // Determinar el tipo de API según el tipo de orden
    let apiType;
    try {
      apiType = order.type === "INVOICE" ? "INVOICE" : "HOLD";
    } catch (error) {
      throw error; 
    }

    try {
      await KitchenService.updateOrderStatus([orderId], "completed", apiType);
    } catch (updateError) {
      console.error(
        "❌ Error al actualizar el estado de la orden:",
        updateError
      );
      throw updateError; 
    }

    try {
      await fetchOrders();
    } catch (fetchError) {
      throw fetchError; 
    }

  } catch (error) {
    console.error("❌ Error general en handleOrderComplete:", error);
    window.toastr?.["error"](
      `Error al completar la orden: ${error.message || "Error desconocido"}`
    );
  }
}

function startPolling() {
  stopPolling();
  activeTab.value = "active"; // Reset to active tab
  refreshTimer.value = setInterval(() => {
    fetchOrders();
    activeTab.value = "active"; // Maintain active tab
  }, POLL_INTERVAL);
}

function stopPolling() {
  if (refreshTimer.value) {
    clearInterval(refreshTimer.value);
    refreshTimer.value = null;
  }
}

watch(
  () => orders.value,
  () => {
    activeTab.value = "active";
  }
);

// Persist tab selection
// watch(activeTab, (newTab) => {
//   console.log("Tab changed to:", newTab);
// });

const kitchenStore = useKitchenStore(); // <-- Create store reference

onMounted(async () => {
  activeTab.value = "active";

  await fetchOrders();

  if (orders.value.length) {
    kitchenStore.$patch({
      orders: orders.value,
      orderIds: orders.value.map((o) => o.id),
    });
  }

  if (autoRefresh.value) {
    startPolling();
  }
});

onUnmounted(() => {
  stopPolling();
});

// Propiedades computadas
const paginatedOrders = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage.value;
  const end = start + itemsPerPage.value;
  return completedOrders.value.slice(start, end);
});

const totalPages = computed(() => {
  return Math.ceil(completedOrders.value.length / itemsPerPage.value);
});

// Propiedades computadas
const paginatedKitchenOrders = computed(() => {
  const start = (currentKitchenPage.value - 1) * itemsPerPage.value;
  const end = start + itemsPerPage.value;
  return kitchenOrders.value.slice(start, end);
});

const totalKitchenPages = computed(() => {
  return Math.ceil(kitchenOrders.value.length / itemsPerPage.value);
});
</script>

<style scoped>
.kitchen-display {
  height: 100vh;
  background-color: var(--v-background);
  display: flex;
  flex-direction: column;
}

.v-container {
  flex: 1;
  overflow-y: auto;
  height: calc(100vh - 64px);
}

.orders-count {
  font-weight: 600;
}

:deep(.v-tab) {
  text-transform: none;
  letter-spacing: normal;
}

@media (max-width: 600px) {
  .text-h4 {
    font-size: 1.5rem !important;
  }
}
</style>
